<template>
  <v-container fluid class="pa-0">
    <!-- Filter Component (includes chips) -->
    <SalesFilter />

    <v-empty-state
      icon="mdi-calendar"
      class="w-100 align-center justify-center"
      v-if="!hasData && !loading"
    >
      <template v-slot:headline>
        <div class="text-h4">No sales data found</div>
      </template>
    </v-empty-state>

    <!-- Edit/Create Dialog with SalesForm -->
    <v-dialog
      v-model="dialogOpen"
      max-width="600"
      :fullscreen="mobile"
      :scrim="false"
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar dark color="indigo" density="compact">
          <v-toolbar-title>{{
            editingRecord ? `Edit Sale Record #${editingRecord.id}` : "New Sale Record"
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <!-- <v-btn variant="text" @click="closeEditDialog"> Close </v-btn> -->
            <v-btn icon dark @click="closeEditDialog">
              <v-icon>mdi-close</v-icon>
              <v-tooltip activator="parent" location="bottom">Close</v-tooltip>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="pa-0">
          <SalesForm :is-admin="isAdmin" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" :color="snackbar.color">
      {{ snackbar.text }}
    </v-snackbar>

    <v-row>
      <v-col cols="12">
        <v-alert v-if="error" type="error" class="mb-4">
          {{ error }}
        </v-alert>

        <v-alert v-if="!salesData && !loading" type="info" class="mb-4 mx-4">
          No sales data available. Start by submitting sales records.
        </v-alert>

        <div v-if="loading" class="text-center">
          <v-progress-circular indeterminate></v-progress-circular>
          <div class="mt-2 mx-4">Loading sales data...</div>
        </div>

        <div v-else-if="hasData" class="sales-matrix">
          <!-- Selection Toolbar -->
          <v-card
            v-if="selectedRecords.length > 0"
            color="primary"
            variant="tonal"
            class="ma-4 pa-3"
          >
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-1">{{ selectedRecords.length }} record(s) selected</span>
              <div class="d-flex align-center ga-2">
                <v-btn color="error" variant="flat" size="small" @click="confirmBulkDeleteDialog">
                  <v-icon start>mdi-delete</v-icon>
                  Delete Selected
                </v-btn>
                <v-btn variant="outlined" size="small" @click="selectedRecords = []">
                  Clear Selection
                </v-btn>
              </div>
            </div>
          </v-card>

          <v-alert v-if="hasActiveFilters" type="info" density="compact" class="my-4 mx-4" closable>
            <div v-if="totalsSummary">
              <span class="mr-3"
                >Total amount: <b>{{ formatCurrency(totalsSummary.amount) }}</b></span
              >
              <span class="mr-3"
                >Average per day: <b>{{ formatCurrency(totalsSummary.amount_avg) }}</b> ({{
                  totalsSummary.number_of_days
                }}
                days)</span
              >
              <span class="mr-3"
                >Orders: <b>{{ totalsSummary.orders }}</b>
                <span class="text-disabled"> (excluded: {{ totalsSummary.excludedOrders }})</span>
              </span>
              <span
                >Records: <b>{{ totalsSummary.records }}</b></span
              >
            </div>

            <template v-if="exporting">
              <v-progress-linear indeterminate class="mt-2 mx-4"></v-progress-linear>
              <div class="text-caption mt-1">Exporting to CSV...</div>
            </template>
          </v-alert>

          <v-data-table-server
            v-model="selectedRecords"
            :headers="tableHeaders"
            :items="salesData"
            :items-length="totalItems"
            :items-per-page="tableOptions.itemsPerPage"
            :page="tableOptions.page"
            :loading="loading"
            :row-props="getRowProps"
            item-value="id"
            show-select
            fixed-header
            height="calc(100vh - 160px)"
            @update:options="onTableOptionsUpdate"
            density="comfortable"
            class="elevation-0"
          >
            <template v-slot:item.actions="{ item }">
              <v-menu location="bottom end">
                <template v-slot:activator="{ props }">
                  <v-btn icon size="small" variant="text" v-bind="props">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list density="compact" nav>
                  <v-list-item @click="editRecord(item)" :disabled="!canEditRecord(item)">
                    <template v-slot:prepend>
                      <v-icon size="small">mdi-pencil</v-icon>
                    </template>
                    <v-list-item-title>
                      {{
                        canEditRecord(item) ? "Edit record" : "You can only edit your own records"
                      }}
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click="confirmDeleteDialog(item)"
                    :disabled="!canDeleteRecord(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="red" size="small">mdi-delete</v-icon>
                    </template>
                    <v-list-item-title>
                      {{
                        canDeleteRecord(item)
                          ? "Delete record"
                          : "You can only delete your own records"
                      }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <template v-slot:item.platform="{ item }">
              <v-chip size="small" label>
                <div
                  :style="{
                    background: getPlatformGradient(item.platform),
                    width: '14px',
                    height: '14px',
                    borderRadius: '3px',
                    marginRight: '6px',
                  }"
                ></div>
                {{ item.platform }}
              </v-chip>
            </template>

            <template v-slot:item.amount="{ item }">
              <span class="text-right d-block">{{ formatCurrency(item.amount) }}</span>
            </template>

            <template v-slot:item.orders="{ item }">
              <span class="text-right d-block" :class="item.orders == 999 ? 'text-disabled' : ''">
                {{ item.orders }}
              </span>
            </template>

            <template v-slot:item.sales_date="{ item }">
              {{ formatDate(item.sales_date) }}
            </template>

            <template v-slot:item.source="{ item }">
              {{ item.source || "manual" }}
            </template>

            <template v-slot:item.created_at="{ item }">
              {{ formatSQLiteUTCDate(item.created_at) }}
            </template>
          </v-data-table-server>

          <!-- NET sales note -->
          <div class="text-caption text-disabled text-right mt-2 mr-4">
            Sales are NET from January 2026 onwards
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed, watch, watchEffect } from "vue";
import { useDisplay } from "vuetify";
import { useFilter } from "@/composables/useFilter.js";
import { useApp } from "@/composables/useApp.js";
import { useSalesForm } from "@/composables/useSalesForm.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog.js";
import { useSalesMenu } from "@/composables/useSalesMenu.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";
import api from "@/utils/api.js";
import SalesFilter from "@/components/SalesFilter.vue";
import SalesForm from "@/components/SalesForm.vue";
import { usePlatformColors } from "@/composables/usePlatformColors.js";

const {
  formatCurrency,
  activeFilters,
  toggleFilterExpand,
  hasActiveFilters,
  numberOfActiveFilters,
  shallowEqual,
  filterTotals,
  totalsSummary,
} = useFilter();

const { user, isAdmin, options } = useApp();
const { mobile } = useDisplay();
const { setActions, clearActions } = useAppBarActions();
const { confirm } = useConfirmDialog();

// Import sales form composable
const { dialogOpen, openEditDialog, closeDialog, completeEdit, resetFormState } = useSalesForm();

// Get refresh trigger from sales menu (triggered after import)
const { salesDataRefreshTrigger, exportCsvHandler } = useSalesMenu();

// Reactive state
const salesData = ref([]);
const loading = ref(false);
const error = ref("");
const exporting = ref(false);
const deleting = ref(false);
const editingRecord = ref(null);
const selectedRecords = ref([]);

// Pagination state
const totalItems = ref(0);
const tableOptions = ref({
  page: 1,
  itemsPerPage: 100,
});

// Table headers
const tableHeaders = [
  { title: "", key: "actions", sortable: false, width: "50px" },
  { title: "ID", key: "id", sortable: false },
  { title: "City", key: "city", sortable: false },
  { title: "Location", key: "location", sortable: false },
  { title: "Brand", key: "brand", sortable: false },
  { title: "Platform", key: "platform", sortable: false },
  { title: "Amount", key: "amount", sortable: false, align: "end" },
  { title: "Orders", key: "orders", sortable: false, align: "end" },
  { title: "Date", key: "sales_date", sortable: false },
  { title: "Source", key: "source", sortable: false },
  { title: "Entered (UTC)", key: "created_at", sortable: false },
  { title: "Reporter", key: "reporter_email", sortable: false },
];

const { getPlatformGradient } = usePlatformColors();

// Row styling for noon orders
const getRowProps = ({ item }) => {
  if (item.source === "noon orders") {
    return { style: "background-color: rgba(251, 140, 0, 0.15)" };
  }
  return {};
};

// Handle pagination changes - only fetch if page or itemsPerPage changed
const onTableOptionsUpdate = async (options) => {
  // Prevent fetch during loading
  if (loading.value) return;

  const pageChanged = options.page !== tableOptions.value.page;
  const itemsPerPageChanged = options.itemsPerPage !== tableOptions.value.itemsPerPage;

  if (pageChanged || itemsPerPageChanged) {
    tableOptions.value.page = options.page;
    tableOptions.value.itemsPerPage = options.itemsPerPage;
    await fetchSalesRawData();
  }
};

// Snackbar for notifications
const snackbar = ref({
  show: false,
  text: "",
  timeout: 3000,
  color: "success",
});

watch(activeFilters, async (val, oldVal) => {
  if (!shallowEqual(val, oldVal)) {
    // Reset to page 1 when filters change
    tableOptions.value.page = 1;
    await fetchSalesRawData();
  }
});

// Watch for dialog close to refresh data
watch(dialogOpen, async (isOpen) => {
  if (!isOpen) {
    // Dialog was closed, refresh data
    await fetchSalesRawData();
  }
});

// Watch for import completion to refresh data
watch(salesDataRefreshTrigger, async () => {
  await fetchSalesRawData();
});

onMounted(async () => {
  exportCsvHandler.value = exportToCSV;
  try {
    await fetchSalesRawData();
  } catch (err) {
    console.error("Failed to initialize:", err);
  }
});

const fetchSalesRawData = async () => {
  loading.value = true;
  error.value = "";

  try {
    const params = {
      ...activeFilters.value,
      page: tableOptions.value.page,
      itemsPerPage: tableOptions.value.itemsPerPage,
    };
    const response = await api.accountingApi.get("/api/accounting/sales", { params });
    salesData.value = response.data.data;
    totalItems.value = response.data.totalItems || 0;
    filterTotals.value = response.data.totals;
  } catch (err) {
    console.error("Failed to load sales data:", err);
    error.value = err.response?.data?.error || "Failed to load sales data";
    throw err;
  } finally {
    loading.value = false;
  }
};

const hasData = computed(() => {
  return totalItems.value > 0;
});

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString();
};

function formatSQLiteUTCDate(dateString) {
  // SQLite dates might come as "2025-12-23 22:30:59" or "2025-12-23T22:30:59Z"
  let date;

  // Handle both formats
  if (dateString.includes("T")) {
    date = new Date(dateString);
  } else {
    // Convert SQLite format to ISO format
    date = new Date(dateString.replace(" ", "T") + "Z");
  }

  // Extract UTC components
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear().toString().slice(-2);
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const seconds = date.getUTCSeconds().toString().padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} UTC`;
}

// Check if user can edit a record
const canEditRecord = (record) => {
  return isAdmin.value || record.reporter_email === user.value?.email;
};

// Check if user can delete a record
const canDeleteRecord = (record) => {
  return isAdmin.value || record.reporter_email === user.value?.email;
};

// Edit record - open in dialog using composable
const editRecord = (record) => {
  if (!canEditRecord(record)) {
    showSnackbar("You can only edit your own records", "error");
    return;
  }

  editingRecord.value = record;
  openEditDialog(record, "rawdata");
};

// Close edit dialog
const closeEditDialog = () => {
  closeDialog();
  editingRecord.value = null;
};

// Open delete confirmation dialog
const confirmDeleteDialog = async (record) => {
  if (!canDeleteRecord(record)) {
    showSnackbar("You can only delete your own records", "error");
    return;
  }

  const confirmed = await confirm({
    title: "Confirm Delete",
    htmlMessage: `Are you sure you want to delete sale record #${record.id}?<div class="mt-2"><strong>${record.brand}</strong> - ${formatCurrency(record.amount)} AED<br />${record.city}, ${record.location}<br />Date: ${formatDate(record.sales_date)}</div>`,
    type: "danger",
    confirmText: "Delete",
  });
  if (!confirmed) return;

  deleting.value = true;
  error.value = "";
  try {
    await api.accountingApi.delete(`/api/accounting/sales/${record.id}`);
    showSnackbar(`Record #${record.id} deleted successfully`, "success");
    await fetchSalesRawData();
  } catch (err) {
    console.error("Failed to delete record:", err);
    showSnackbar(err.response?.data?.error || "Failed to delete record", "error");
  } finally {
    deleting.value = false;
  }
};

// Show snackbar message
const showSnackbar = (text, color = "success") => {
  snackbar.value = {
    show: true,
    text,
    color,
    timeout: 3000,
  };
};

// Confirm and bulk delete records
const confirmBulkDeleteDialog = async () => {
  const confirmed = await confirm({
    title: "Confirm Bulk Delete",
    message: `Are you sure you want to delete ${selectedRecords.value.length} record(s)?`,
    type: "danger",
    confirmText: `Delete ${selectedRecords.value.length} Records`,
    warningText: "This action cannot be undone.",
  });
  if (!confirmed) return;

  deleting.value = true;
  error.value = "";
  try {
    const response = await api.accountingApi.post("/api/accounting/sales/bulk-delete", {
      ids: selectedRecords.value,
    });
    const { deleted, failed } = response.data;
    if (failed > 0) {
      showSnackbar(`Deleted ${deleted} record(s), ${failed} failed`, "warning");
    } else {
      showSnackbar(`${deleted} record(s) deleted successfully`, "success");
    }
    await fetchSalesRawData();
    selectedRecords.value = [];
  } catch (err) {
    console.error("Failed to bulk delete records:", err);
    showSnackbar(err.response?.data?.error || "Failed to delete records", "error");
  } finally {
    deleting.value = false;
  }
};

// Export functions with CSV
const exportToCSV = async () => {
  if (totalItems.value === 0) {
    showSnackbar("No data available to export", "error");
    return;
  }

  // Build confirmation message with filter info
  const filterInfo = hasActiveFilters.value
    ? `<p class="text-warning mb-2"><strong>Filters are active.</strong> Only filtered data will be exported.</p>`
    : "";

  const confirmed = await confirm({
    title: "Export to CSV",
    type: "info",
    htmlMessage: `
      ${filterInfo}
      <p>The following data will be exported:</p>
      <ul class="mt-2">
        <li><strong>${totalItems.value}</strong> records</li>
        <li><strong>${totalsSummary.value?.amount ? formatCurrency(totalsSummary.value.amount) : 0}</strong> AED total</li>
      </ul>
    `,
    confirmText: "Export",
    cancelText: "Cancel",
  });

  if (!confirmed) return;

  exporting.value = true;
  try {
    // Fetch all records (no pagination) for export
    const response = await api.accountingApi.get("/api/accounting/sales", { params: activeFilters.value });
    const dataToExport = response.data.data;
    generateCSVFile(dataToExport);
    showSnackbar("CSV exported successfully", "success");
  } catch (err) {
    console.error("Export failed:", err);
    showSnackbar("Failed to export data", "error");
  } finally {
    exporting.value = false;
  }
};

const generateCSVFile = (data) => {
  // Define headers
  const headers = [
    "ID",
    "Country",
    "City",
    "Location",
    "Brand",
    "Platform",
    "Amount",
    "Orders",
    "Sales Date",
    "Source",
    "Created At",
    "Reporter Email",
  ];

  // Convert data to CSV rows
  const csvRows = [
    headers.join(","),
    ...data.map((item) =>
      [
        item.id,
        escapeCSV(item.country || ""),
        escapeCSV(item.city),
        escapeCSV(item.location),
        escapeCSV(item.brand),
        escapeCSV(item.platform),
        item.amount,
        item.orders,
        escapeCSV(formatDateForExcel(item.sales_date)),
        escapeCSV(item.source || "manual"),
        escapeCSV(formatDateForExcel(item.created_at)),
        escapeCSV(item.reporter_email),
      ].join(","),
    ),
  ];

  // Create CSV content
  const csvContent = csvRows.join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `sales_data_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeCSV = (value) => {
  if (value === null || value === undefined) return '""';
  const stringValue = String(value);
  if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const formatDateForExcel = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-CA");
};

// Open create dialog
const openCreateDialog = () => {
  editingRecord.value = null;
  resetFormState();
  dialogOpen.value = true;
};

// App bar actions
watchEffect(() => {
  setActions([
    {
      icon: "mdi-filter-variant",
      tooltip: "Filters",
      onClick: toggleFilterExpand,
      badge:
        numberOfActiveFilters.value > 0
          ? { content: numberOfActiveFilters.value, show: true, color: "error" }
          : null,
    },
    {
      icon: "mdi-plus",
      tooltip: "Add Sale",
      onClick: openCreateDialog,
    },
  ]);
});
onBeforeUnmount(() => {
  clearActions();
  exportCsvHandler.value = null;
});
</script>

<style scoped>
:deep(thead th) {
  font-weight: 700 !important;
}
</style>
